import { pgTable, serial, integer, text, timestamp, numeric, date, boolean, uniqueIndex, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Effective-dated employment history. One row per continuous stint; a rehire
// opens a new row instead of overwriting employees.hire_date. Maintained by a
// DB trigger on employees (migration 0129) so every write path (UI, census
// import, bulk import, rehire/terminate) keeps it consistent.
export const employeeEmploymentPeriods = pgTable("employee_employment_periods", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull(),
  clientId: integer("client_id"),
  startDate: date("start_date").notNull(),
  endDate: date("end_date"), // null = currently employed
  source: text("source").notNull().default("trigger"), // trigger | backfill | manual
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  byEmployee: index("employee_employment_periods_emp_idx").on(t.employeeId, t.startDate),
}));

// Effective-dated compensation. employees.compensation_* stays as the
// "current" mirror; this is the queryable history the ACA rate-of-pay safe
// harbor reads month by month.
export const employeeCompensationHistory = pgTable("employee_compensation_history", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull(),
  clientId: integer("client_id"),
  effectiveDate: date("effective_date").notNull(),
  compensationType: text("compensation_type"), // hourly | salary
  amount: numeric("amount", { precision: 12, scale: 2 }), // hourly rate or annual salary
  payCycle: text("pay_cycle"),
  scheduledHoursPerWeek: numeric("scheduled_hours_per_week", { precision: 5, scale: 2 }),
  source: text("source").notNull().default("manual"), // manual | trigger | backfill | gusto | import
  sourceRef: text("source_ref"), // e.g. Gusto compensation uuid
  reason: text("reason"),
  createdByUserId: text("created_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("employee_compensation_history_unique").on(t.employeeId, t.effectiveDate),
}));

// Form W-2 Box 1 wages per tax year — the only input the W-2 affordability
// safe harbor may use. NULL box1 = "Pending Data", never an assumed value.
export const employeeW2Wages = pgTable("employee_w2_wages", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull(),
  clientId: integer("client_id").notNull(),
  taxYear: integer("tax_year").notNull(),
  box1Wages: numeric("box1_wages", { precision: 12, scale: 2 }),
  isFinal: boolean("is_final").notNull().default(false),
  source: text("source").notNull().default("manual"), // manual | payroll_import
  enteredByUserId: text("entered_by_user_id"),
  enteredAt: timestamp("entered_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  uniq: uniqueIndex("employee_w2_wages_unique").on(t.employeeId, t.taxYear),
}));

export const insertEmployeeCompensationHistorySchema = createInsertSchema(employeeCompensationHistory)
  .omit({ id: true, createdAt: true });
export const insertEmployeeW2WagesSchema = createInsertSchema(employeeW2Wages)
  .omit({ id: true, enteredAt: true, updatedAt: true });
export const insertEmployeeEmploymentPeriodSchema = createInsertSchema(employeeEmploymentPeriods)
  .omit({ id: true, createdAt: true, updatedAt: true });

export type EmployeeEmploymentPeriod = typeof employeeEmploymentPeriods.$inferSelect;
export type EmployeeCompensationHistory = typeof employeeCompensationHistory.$inferSelect;
export type InsertEmployeeCompensationHistory = z.infer<typeof insertEmployeeCompensationHistorySchema>;
export type EmployeeW2Wages = typeof employeeW2Wages.$inferSelect;
export type InsertEmployeeW2Wages = z.infer<typeof insertEmployeeW2WagesSchema>;
